import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import BenefitsData from './sections/BenefitsData/BenefitsData';
import ChallengeData from './sections/ChallengeData/ChallengeData';
import ChallengesSummaryData from './sections/ChallengesSummaryData/ChallengesSummaryData';
import CoverageData from './sections/CoverageData/CoverageData';
import DonationsData from './sections/DonationsData/DonationsData';
import HabitsData from './sections/HabitsData/HabitsData';
import HealthOnboardingData from './sections/HealthOnboardingData/HealthOnboardingData';
import HealthReportFooter from './sections/HealthReportFooter/HealthReportFooter';
import Navigation from './sections/Navigation/Navigation';
import RefundData from './sections/RefundData/RefundData';
import RefundSuccessData from './sections/RefundSuccessData/RefundSuccessData';
import ReportHeader from './sections/ReportHeader/ReportHeader';
import TelemedicineData from './sections/TelemedicineData/TelemedicineData';
import TelemedicineInsight from './sections/TelemedicineInsight/TelemedicineInsight';
import WellbeingSummaryData from './sections/WellbeingSummaryData/WellbeingSummaryData';
import LoadingPage from '../../../../shared/components/LoadingPage/LoadingPage';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import { REPORTS_ROUTE } from '../../../../shared/constants/auth';
import { CLICK_ACTION, DOWNLOAD_HEALTH_REPORT } from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { IHealthReport, IHealthReportList } from '../../../../shared/interfaces/reports';
import Company from '../../../../shared/services/api/companies/companies';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { TRANSLATIONS_REPORTS } from '../../../../shared/translations/reports';
import { getAndRemoveLocalStorage } from '../../../../shared/utils/utils';

import './HealthReport.scss';

export const HealthReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [reportData, setReportData] = useState<IHealthReport>();
  const [reportFromList, setReportFromList] = useState<IHealthReportList>();
  const [inDownloadMode, setInDownloadMode] = useState(false);
  const { companyData, isLoadingCompanyData, isMexico } = useContext(CompanyContext);

  const healthInsuranceRef = useRef<HTMLDivElement>(null);
  const habitsAndBenefitsRef = useRef<HTMLDivElement>(null);
  const communityAndImpactRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const pageHeight = useMemo(() => {
    return isMexico ? 5760 : 6880;
  }, [isMexico]);

  const contentRef = useRef<HTMLDivElement>(null);
  const downloadReport = useReactToPrint({
    contentRef,
    pageStyle: `
      @page {
        size: 1344px ${pageHeight}px;
        margin: 0mm !important;
      }
    `,
    onBeforePrint: async () => setInDownloadMode(true),
    onAfterPrint: () => setInDownloadMode(false)
  });

  const handleDownloadReport = useCallback(() => {
    TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_HEALTH_REPORT);
    downloadReport();
  }, [downloadReport]);

  useEffect(() => {
    const tableData = getAndRemoveLocalStorage('reportData');
    if (tableData) {
      const parsedData: IHealthReportList = JSON.parse(tableData);
      setReportFromList(parsedData);
    } else {
      navigate(REPORTS_ROUTE);
    }
  }, []);

  const isValidCompany = useCallback(() => {
    return companyData?.id !== '' && companyData?.locale;
  }, [companyData]);

  const fetchHealthReportData = useCallback(
    async (data: IHealthReportList) => {
      setLoading(true);
      setError(false);
      try {
        const healthReport = await Company.getCompanyHealthReportData(companyData.id, data.endDate);
        if (healthReport) {
          setReportData(healthReport);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [companyData.id]
  );

  useEffect(() => {
    if (isValidCompany() && !isLoadingCompanyData && reportFromList) {
      fetchHealthReportData(reportFromList);
    }
  }, [companyData, isLoadingCompanyData, isValidCompany, fetchHealthReportData, reportFromList]);

  if (error) {
    return (
      <ErrorCard
        primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
        secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
        action={() => (reportFromList ? fetchHealthReportData(reportFromList) : null)}
        center={true}
        buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        className="hr-error-container"
      />
    );
  }

  if (!reportData || loading || !reportFromList) {
    return <LoadingPage />;
  }

  return (
    <div className="health-report" data-experience="new">
      <Navigation
        items={[
          { label: t(TRANSLATIONS_REPORTS.healthReport.navigation.healthInsurance), anchor: healthInsuranceRef },
          { label: t(TRANSLATIONS_REPORTS.healthReport.navigation.habitsAndBenefits), anchor: habitsAndBenefitsRef },
          { label: t(TRANSLATIONS_REPORTS.healthReport.navigation.communityAndImpact), anchor: communityAndImpactRef }
        ]}
      />
      <div
        ref={contentRef}
        className="hr-landing-page-container"
        data-experience="new"
        data-testid="health-report-container"
      >
        <ReportHeader reportList={reportFromList} downloadReport={handleDownloadReport} />
        <CoverageData data={reportData} isMexico={Boolean(isMexico)} ref={healthInsuranceRef} />
        {!isMexico && <RefundData data={reportData} inDownloadMode={inDownloadMode} />}
        {isMexico && <TelemedicineData data={reportData} inDownloadMode={inDownloadMode} />}
        {isMexico ? (
          <TelemedicineInsight
            data={reportData}
            isLastReport={reportFromList.isLastGenerated}
            inDownloadMode={inDownloadMode}
          />
        ) : (
          <RefundSuccessData
            data={reportData}
            isLastReport={reportFromList.isLastGenerated}
            inDownloadMode={inDownloadMode}
          />
        )}
        <HealthOnboardingData data={reportData} ref={habitsAndBenefitsRef} />
        <HabitsData data={reportData} />
        <BenefitsData data={reportData} isMexico={Boolean(isMexico)} />
        <WellbeingSummaryData
          data={reportData}
          isLastReport={reportFromList.isLastGenerated}
          inDownloadMode={inDownloadMode}
        />
        <ChallengeData data={reportData} ref={communityAndImpactRef} />
        <DonationsData data={reportData} inDownloadMode={inDownloadMode} />
        <ChallengesSummaryData
          data={reportData}
          isLastReport={reportFromList.isLastGenerated}
          inDownloadMode={inDownloadMode}
        />
        <HealthReportFooter reportList={reportFromList} />
      </div>
    </div>
  );
};

export default HealthReport;
